import { useMemo, useState, type FormEvent } from 'react';
import {
  Check,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  ImagePlus,
  Minus,
  Pencil,
  Plus,
  Trash2,
} from 'lucide-react';

import {
  Badge,
  Button,
  Checkbox,
  Drawer,
  ErrorAlert,
  Field,
  Input,
  Select,
  Textarea,
} from '@/components/ui';
import {
  useBrands,
  useCategories,
  useDeleteProductImage,
  useDeleteVariant,
  useProduct,
  useReorderProductImages,
  useSaveProduct,
  useSaveVariant,
  useUpdateProductImage,
  useUploadProductImage,
  toCategoryTree,
  type AdminBrand,
  type AdminCategory,
  type AdminProductDetail,
  type PriceTier,
  type ProductStatus,
  type ProductVariant,
} from '@/lib/catalog-api';
import { apiBaseOrigin, siteOrigin } from '@/lib/env';
import {
  DESCRIPTION_RANGE,
  SITE_NAME,
  TITLE_RANGE,
  buildSeoPreview,
  hostOf,
  slugify,
  truncate,
} from '@/lib/product-seo';
import { cn, errorMessage, fieldErrors, formatCurrency, formatNumber } from '@/lib/utils';

const STATUSES: ProductStatus[] = ['DRAFT', 'ACTIVE', 'INACTIVE', 'ARCHIVED'];

interface FormState {
  name: string;
  sku: string;
  categoryId: string;
  brandId: string;
  status: ProductStatus;
  shortDescription: string;
  description: string;
  material: string;
  color: string;
  size: string;
  hsnCode: string;
  moq: string;
  maxOrderQuantity: string;
  quantityIncrement: string;
  basePrice: string;
  wholesalePrice: string;
  gstPercent: string;
  isFeatured: boolean;
  isNewArrival: boolean;
  isBestSeller: boolean;

  // --- SEO ---------------------------------------------------------------
  // Every text field here is an override stored as typed. Blank is meaningful:
  // it means "let the storefront derive this from the product", which is the
  // right answer for most products, so none of them is required.
  metaTitle: string;
  metaDescription: string;
  metaKeywords: string;
  focusKeyword: string;
  canonicalUrl: string;
  ogTitle: string;
  ogDescription: string;
  ogImageUrl: string;
  noIndex: boolean;
  noFollow: boolean;
}

const EMPTY_FORM: FormState = {
  name: '',
  sku: '',
  categoryId: '',
  brandId: '',
  status: 'DRAFT',
  shortDescription: '',
  description: '',
  material: '',
  color: '',
  size: '',
  hsnCode: '',
  moq: '',
  maxOrderQuantity: '',
  quantityIncrement: '1',
  basePrice: '',
  wholesalePrice: '',
  gstPercent: '',
  isFeatured: false,
  isNewArrival: false,
  isBestSeller: false,
  metaTitle: '',
  metaDescription: '',
  metaKeywords: '',
  focusKeyword: '',
  canonicalUrl: '',
  ogTitle: '',
  ogDescription: '',
  ogImageUrl: '',
  noIndex: false,
  noFollow: false,
};

function toFormState(product: AdminProductDetail): FormState {
  return {
    name: product.name,
    sku: product.sku,
    categoryId: product.category?.id ?? '',
    brandId: product.brand?.id ?? '',
    status: product.status,
    shortDescription: product.shortDescription ?? '',
    description: product.description ?? '',
    material: product.material ?? '',
    color: product.color ?? '',
    size: product.size ?? '',
    hsnCode: product.hsnCode ?? '',
    moq: String(product.moq),
    maxOrderQuantity: product.maxOrderQuantity === null ? '' : String(product.maxOrderQuantity),
    quantityIncrement: String(product.quantityIncrement),
    basePrice: String(product.basePrice),
    wholesalePrice: String(product.wholesalePrice),
    gstPercent: String(product.gstPercent),
    isFeatured: product.isFeatured,
    isNewArrival: product.isNewArrival,
    isBestSeller: product.isBestSeller,
    metaTitle: product.seo.metaTitle ?? '',
    metaDescription: product.seo.metaDescription ?? '',
    metaKeywords: product.seo.metaKeywords ?? '',
    focusKeyword: product.seo.focusKeyword ?? '',
    canonicalUrl: product.seo.canonicalUrl ?? '',
    ogTitle: product.seo.ogTitle ?? '',
    ogDescription: product.seo.ogDescription ?? '',
    ogImageUrl: product.seo.ogImageUrl ?? '',
    noIndex: product.seo.noIndex,
    noFollow: product.seo.noFollow,
  };
}

/** Empty string → omitted; otherwise a number. Keeps `0` meaningful. */
function optionalNumber(value: string): number | undefined {
  const trimmed = value.trim();
  return trimmed === '' ? undefined : Number(trimmed);
}

/**
 * Create or edit a product.
 *
 * Mounted only while open and keyed by product id, so the form state resets by
 * unmounting rather than by an effect that syncs props into state — reopening
 * on a different product can never show the previous one's values.
 */
export function ProductFormDrawer({
  productId,
  open,
  onClose,
}: {
  /** `null` creates; an id edits. */
  productId: string | null;
  open: boolean;
  onClose: () => void;
}) {
  if (!open) {
    return null;
  }

  return <ProductFormBody key={productId ?? 'new'} productId={productId} onClose={onClose} />;
}

function ProductFormBody({
  productId,
  onClose,
}: {
  productId: string | null;
  onClose: () => void;
}) {
  const isEdit = productId !== null;

  const detail = useProduct(productId);
  const categories = useCategories();
  const brands = useBrands();
  const save = useSaveProduct();

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? 'Edit product' : 'New product'}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          {/* Disabled exactly while the form is not rendered below — a submit
              with no `#product-form` in the DOM does nothing. Deliberately not
              `detail.isPending`: a disabled query (`enabled: productId !== null`)
              sits at `pending` forever in React Query v5, which left the create
              button dead no matter how the form was filled. */}
          <Button
            type="submit"
            form="product-form"
            disabled={save.isPending || (isEdit && !detail.data)}
          >
            {save.isPending ? 'Saving…' : isEdit ? 'Save changes' : 'Create product'}
          </Button>
        </div>
      }
    >
      {isEdit && !detail.data ? (
        <p className="text-sm text-ink-subtle">
          {detail.isError ? errorMessage(detail.error) : 'Loading product…'}
        </p>
      ) : (
        <ProductFields
          product={detail.data ?? null}
          categories={categories.data ?? []}
          brands={brands.data ?? []}
          save={save}
          onSaved={onClose}
        />
      )}
    </Drawer>
  );
}

function ProductFields({
  product,
  categories,
  brands,
  save,
  onSaved,
}: {
  product: AdminProductDetail | null;
  categories: AdminCategory[];
  brands: AdminBrand[];
  save: ReturnType<typeof useSaveProduct>;
  onSaved: () => void;
}) {
  // Lazy initialisers: this component only mounts once its product has loaded,
  // so the initial values are correct without a synchronising effect.
  const [form, setForm] = useState<FormState>(() => (product ? toFormState(product) : EMPTY_FORM));
  const [tiers, setTiers] = useState<PriceTier[]>(() =>
    (product?.priceTiers ?? []).map(({ minQuantity, maxQuantity, pricePerUnit }) => ({
      minQuantity,
      maxQuantity,
      pricePerUnit,
    })),
  );
  const [specs, setSpecs] = useState<Array<{ name: string; value: string }>>(() =>
    (product?.specifications ?? []).map(({ name, value }) => ({ name, value })),
  );

  const errors = fieldErrors(save.error);
  const categoryOptions = toCategoryTree(categories);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]): void =>
    setForm((previous) => ({ ...previous, [key]: value }));

  const handleSubmit = (event: FormEvent): void => {
    event.preventDefault();

    const body: Record<string, unknown> = {
      name: form.name,
      sku: form.sku,
      categoryId: form.categoryId,
      brandId: form.brandId || null,
      status: form.status,
      shortDescription: form.shortDescription || null,
      description: form.description || null,
      material: form.material || null,
      color: form.color || null,
      size: form.size || null,
      hsnCode: form.hsnCode || null,
      moq: optionalNumber(form.moq),
      maxOrderQuantity: form.maxOrderQuantity.trim() === '' ? null : Number(form.maxOrderQuantity),
      quantityIncrement: optionalNumber(form.quantityIncrement),
      basePrice: Number(form.basePrice),
      wholesalePrice: Number(form.wholesalePrice),
      gstPercent: optionalNumber(form.gstPercent),
      isFeatured: form.isFeatured,
      isNewArrival: form.isNewArrival,
      isBestSeller: form.isBestSeller,
      // SEO overrides. `|| null` rather than the empty string: clearing a field
      // has to mean "fall back to the product" again, and `''` would instead
      // store a blank title that the storefront would then render.
      metaTitle: form.metaTitle.trim() || null,
      metaDescription: form.metaDescription.trim() || null,
      metaKeywords: form.metaKeywords.trim() || null,
      focusKeyword: form.focusKeyword.trim() || null,
      canonicalUrl: form.canonicalUrl.trim() || null,
      ogTitle: form.ogTitle.trim() || null,
      ogDescription: form.ogDescription.trim() || null,
      ogImageUrl: form.ogImageUrl.trim() || null,
      noIndex: form.noIndex,
      noFollow: form.noFollow,
      // Both collections replace in full — the server treats them that way, so
      // sending the complete set is the only correct thing a client can do.
      priceTiers: tiers.map((tier) => ({
        minQuantity: tier.minQuantity,
        maxQuantity: tier.maxQuantity,
        pricePerUnit: tier.pricePerUnit,
      })),
      specifications: specs.filter((spec) => spec.name.trim() && spec.value.trim()),
    };

    save.mutate({ productId: product?.id ?? null, body }, { onSuccess: onSaved });
  };

  return (
    <form id="product-form" onSubmit={handleSubmit} className="space-y-5">
      {save.isError && (
        <ErrorAlert title="Could not save this product">{errorMessage(save.error)}</ErrorAlert>
      )}

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Basics</h3>

        <Field label="Product name" htmlFor="name" required error={errors['name']}>
          <Input
            id="name"
            value={form.name}
            onChange={(event) => update('name', event.target.value)}
            required
            minLength={2}
            invalid={Boolean(errors['name'])}
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="SKU" htmlFor="sku" required error={errors['sku']}>
            <Input
              id="sku"
              value={form.sku}
              onChange={(event) => update('sku', event.target.value)}
              required
              invalid={Boolean(errors['sku'])}
            />
          </Field>

          <Field label="Status" htmlFor="status" error={errors['status']}>
            <Select
              id="status"
              value={form.status}
              onChange={(event) => update('status', event.target.value as ProductStatus)}
            >
              {STATUSES.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Category" htmlFor="categoryId" required error={errors['categoryId']}>
            <Select
              id="categoryId"
              value={form.categoryId}
              onChange={(event) => update('categoryId', event.target.value)}
              required
              invalid={Boolean(errors['categoryId'])}
            >
              <option value="">Select a category…</option>
              {categoryOptions.map((category) => (
                <option key={category.id} value={category.id}>
                  {'  '.repeat(category.depth)}
                  {category.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Brand" htmlFor="brandId" error={errors['brandId']}>
            <Select
              id="brandId"
              value={form.brandId}
              onChange={(event) => update('brandId', event.target.value)}
            >
              <option value="">No brand</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>
                  {brand.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>

        <Field label="Short description" htmlFor="shortDescription">
          <Textarea
            id="shortDescription"
            rows={2}
            value={form.shortDescription}
            onChange={(event) => update('shortDescription', event.target.value)}
          />
        </Field>

        <Field label="Full description" htmlFor="description">
          <Textarea
            id="description"
            rows={4}
            value={form.description}
            onChange={(event) => update('description', event.target.value)}
          />
        </Field>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Attributes
        </h3>

        <div className="grid grid-cols-3 gap-3">
          <Field label="Material" htmlFor="material">
            <Input
              id="material"
              value={form.material}
              onChange={(event) => update('material', event.target.value)}
            />
          </Field>
          <Field label="Colour" htmlFor="color">
            <Input
              id="color"
              value={form.color}
              onChange={(event) => update('color', event.target.value)}
            />
          </Field>
          <Field label="Size" htmlFor="size">
            <Input
              id="size"
              value={form.size}
              onChange={(event) => update('size', event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Bulk ordering rules
        </h3>

        <div className="grid grid-cols-3 gap-3">
          <Field
            label="MOQ"
            htmlFor="moq"
            hint={product ? undefined : 'Blank uses the default'}
            error={errors['moq']}
          >
            <Input
              id="moq"
              type="number"
              min={1}
              value={form.moq}
              onChange={(event) => update('moq', event.target.value)}
              invalid={Boolean(errors['moq'])}
            />
          </Field>

          <Field
            label="Max order qty"
            htmlFor="maxOrderQuantity"
            error={errors['maxOrderQuantity']}
          >
            <Input
              id="maxOrderQuantity"
              type="number"
              min={1}
              value={form.maxOrderQuantity}
              onChange={(event) => update('maxOrderQuantity', event.target.value)}
              invalid={Boolean(errors['maxOrderQuantity'])}
            />
          </Field>

          <Field label="Increment" htmlFor="quantityIncrement" error={errors['quantityIncrement']}>
            <Input
              id="quantityIncrement"
              type="number"
              min={1}
              value={form.quantityIncrement}
              onChange={(event) => update('quantityIncrement', event.target.value)}
            />
          </Field>
        </div>
      </section>

      <section className="space-y-3">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Pricing</h3>

        <div className="grid grid-cols-3 gap-3">
          <Field label="List price" htmlFor="basePrice" required error={errors['basePrice']}>
            <Input
              id="basePrice"
              type="number"
              min={0.01}
              step={0.01}
              value={form.basePrice}
              onChange={(event) => update('basePrice', event.target.value)}
              required
              invalid={Boolean(errors['basePrice'])}
            />
          </Field>

          <Field
            label="Wholesale"
            htmlFor="wholesalePrice"
            required
            error={errors['wholesalePrice']}
          >
            <Input
              id="wholesalePrice"
              type="number"
              min={0.01}
              step={0.01}
              value={form.wholesalePrice}
              onChange={(event) => update('wholesalePrice', event.target.value)}
              required
              invalid={Boolean(errors['wholesalePrice'])}
            />
          </Field>

          <Field label="GST %" htmlFor="gstPercent" error={errors['gstPercent']}>
            <Input
              id="gstPercent"
              type="number"
              min={0}
              max={100}
              step={0.01}
              value={form.gstPercent}
              onChange={(event) => update('gstPercent', event.target.value)}
            />
          </Field>
        </div>

        <Field label="HSN code" htmlFor="hsnCode">
          <Input
            id="hsnCode"
            value={form.hsnCode}
            onChange={(event) => update('hsnCode', event.target.value)}
          />
        </Field>
      </section>

      <TierEditor tiers={tiers} onChange={setTiers} errors={errors} />

      <SpecificationEditor specs={specs} onChange={setSpecs} />

      <section className="space-y-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Merchandising
        </h3>
        <Checkbox
          label="Featured"
          checked={form.isFeatured}
          onChange={(event) => update('isFeatured', event.target.checked)}
        />
        <Checkbox
          label="New arrival"
          checked={form.isNewArrival}
          onChange={(event) => update('isNewArrival', event.target.checked)}
        />
        <Checkbox
          label="Best seller"
          checked={form.isBestSeller}
          onChange={(event) => update('isBestSeller', event.target.checked)}
        />
      </section>

      <SeoSection form={form} update={update} errors={errors} product={product} />

      {product && (
        <>
          <VariantManager product={product} />
          <ImageManager product={product} />
        </>
      )}
    </form>
  );
}

/**
 * Search and social metadata (spec §46).
 *
 * The section is built around one idea: every field is an *override*, and a
 * product nobody has optimised still has a complete title, description and
 * social card. So the preview never shows an empty box — it shows what the
 * storefront will actually render, with a note saying which parts are
 * inherited and which the merchandiser has written. The fallback chain itself
 * lives in `lib/product-seo`, next to the tests that pin it to the
 * storefront's.
 */

function CharacterCount({
  value,
  min,
  ideal,
}: {
  value: string;
  min: number;
  ideal: number;
}) {
  const { length } = value;
  const tone =
    length === 0
      ? 'text-ink-subtle'
      : length > ideal
        ? 'text-danger-600'
        : length < min
          ? 'text-warning-700'
          : 'text-success-600';

  return (
    <span className={cn('tabular-nums text-[11px]', tone)}>
      {length} / {ideal}
    </span>
  );
}

/** One pass/fail line in the focus-keyword checklist. */
function KeywordCheck({ passed, children }: { passed: boolean; children: string }) {
  const Icon = passed ? Check : Minus;

  return (
    <li className={cn('flex items-center gap-1.5', passed ? 'text-success-700' : 'text-ink-subtle')}>
      <Icon className="size-3.5 shrink-0" aria-hidden="true" />
      {children}
    </li>
  );
}

function SeoSection({
  form,
  update,
  errors,
  product,
}: {
  form: FormState;
  update: <K extends keyof FormState>(key: K, value: FormState[K]) => void;
  errors: Record<string, string>;
  product: AdminProductDetail | null;
}) {
  // Driven by the live form rather than the saved product, so editing the name
  // or short description above updates the preview as it is typed.
  const name = form.name.trim();
  const moq = optionalNumber(form.moq) ?? product?.moq ?? null;

  // A new product has no slug yet — the server assigns one on save — so preview
  // the slug it would derive from the name.
  const slug = product?.slug ?? slugify(name);

  const preview = useMemo(
    () =>
      buildSeoPreview({
        name,
        shortDescription: form.shortDescription,
        moq,
        slug,
        metaTitle: form.metaTitle,
        metaDescription: form.metaDescription,
        canonicalUrl: form.canonicalUrl,
        ogTitle: form.ogTitle,
        ogDescription: form.ogDescription,
        ogImageUrl: form.ogImageUrl,
        images: product?.images,
      }),
    [form, name, moq, slug, product?.images],
  );

  const focusKeyword = form.focusKeyword.trim().toLowerCase();
  const contains = (haystack: string): boolean =>
    focusKeyword !== '' && haystack.toLowerCase().includes(focusKeyword);

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Search engine listing
        </h3>
        {form.noIndex && (
          <Badge tone="warning">
            <EyeOff className="size-3" aria-hidden="true" />
            Hidden from search
          </Badge>
        )}
      </div>

      {/* What the result looks like on a search page. Rendered from the same
          fallback chain the storefront uses, so a blank form still previews
          the real listing rather than an empty box. */}
      <div className="space-y-1 rounded-lg border border-line bg-canvas px-3 py-3">
        <p className="truncate text-[11px] text-success-700">{preview.url}</p>
        <p className="truncate text-sm font-medium text-brand-700">
          {truncate(preview.title, 65)}
        </p>
        <p className="text-xs leading-relaxed text-ink-muted">
          {truncate(preview.description, 160)}
        </p>

        {(preview.titleInherited || preview.descriptionInherited) && (
          <p className="pt-1 text-[11px] text-ink-subtle">
            Inherited from the product:{' '}
            {[preview.titleInherited && 'title', preview.descriptionInherited && 'description']
              .filter(Boolean)
              .join(' and ')}
            . Fill the fields below to override.
          </p>
        )}
      </div>

      <Field
        label="Meta title"
        htmlFor="metaTitle"
        hint={`Blank uses the product name plus " | ${SITE_NAME}".`}
        error={errors['metaTitle']}
      >
        <Input
          id="metaTitle"
          value={form.metaTitle}
          onChange={(event) => update('metaTitle', event.target.value)}
          maxLength={180}
          placeholder={name || 'Product name'}
          invalid={Boolean(errors['metaTitle'])}
        />
      </Field>
      <div className="-mt-2 flex justify-end">
        <CharacterCount value={form.metaTitle} min={TITLE_RANGE.min} ideal={TITLE_RANGE.ideal} />
      </div>

      <Field
        label="Meta description"
        htmlFor="metaDescription"
        hint="Blank uses the short description."
        error={errors['metaDescription']}
      >
        <Textarea
          id="metaDescription"
          rows={3}
          value={form.metaDescription}
          onChange={(event) => update('metaDescription', event.target.value)}
          maxLength={400}
          placeholder={form.shortDescription.trim() || 'Short description'}
          invalid={Boolean(errors['metaDescription'])}
        />
      </Field>
      <div className="-mt-2 flex justify-end">
        <CharacterCount
          value={form.metaDescription}
          min={DESCRIPTION_RANGE.min}
          ideal={DESCRIPTION_RANGE.ideal}
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field
          label="Focus keyword"
          htmlFor="focusKeyword"
          hint="Editorial only — never rendered."
          error={errors['focusKeyword']}
        >
          <Input
            id="focusKeyword"
            value={form.focusKeyword}
            onChange={(event) => update('focusKeyword', event.target.value)}
            maxLength={120}
            placeholder="wholesale laptop bags"
            invalid={Boolean(errors['focusKeyword'])}
          />
        </Field>

        <Field
          label="Meta keywords"
          htmlFor="metaKeywords"
          hint="Comma separated."
          error={errors['metaKeywords']}
        >
          <Input
            id="metaKeywords"
            value={form.metaKeywords}
            onChange={(event) => update('metaKeywords', event.target.value)}
            maxLength={500}
            placeholder="wholesale bags, bulk backpacks"
            invalid={Boolean(errors['metaKeywords'])}
          />
        </Field>
      </div>

      {/* Shown only once there is a term to check against — an always-visible
          list of failures on a blank form is noise, not guidance. */}
      {focusKeyword !== '' && (
        <ul className="space-y-1 rounded-lg border border-dashed border-line px-3 py-2 text-xs">
          <KeywordCheck passed={contains(preview.title)}>In the title</KeywordCheck>
          <KeywordCheck passed={contains(preview.description)}>In the description</KeywordCheck>
          <KeywordCheck passed={contains(name)}>In the product name</KeywordCheck>
          <KeywordCheck passed={contains(slug)}>In the URL</KeywordCheck>
        </ul>
      )}

      <Field
        label="Canonical URL"
        htmlFor="canonicalUrl"
        hint="Only when this product duplicates another page. Blank uses its own URL."
        error={errors['canonicalUrl']}
      >
        <Input
          id="canonicalUrl"
          type="url"
          value={form.canonicalUrl}
          onChange={(event) => update('canonicalUrl', event.target.value)}
          maxLength={500}
          placeholder={`${siteOrigin}/products/${slug || 'product-slug'}`}
          invalid={Boolean(errors['canonicalUrl'])}
        />
      </Field>

      <div className="space-y-2 pt-1">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Social card
        </h4>

        {/* The same picture-plus-headline a link paste renders. Kept compact:
            it exists to catch a missing image or a truncated headline, not to
            reproduce any one network's chrome. */}
        <div className="overflow-hidden rounded-lg border border-line">
          {preview.social.image ? (
            <img
              src={preview.social.image}
              alt=""
              className="h-32 w-full bg-canvas object-cover"
            />
          ) : (
            <div className="flex h-32 items-center justify-center bg-canvas text-xs text-ink-subtle">
              No image — add a product image or an override below
            </div>
          )}
          <div className="space-y-0.5 px-3 py-2">
            <p className="text-[11px] uppercase tracking-wide text-ink-subtle">
              {hostOf(preview.url)}
            </p>
            <p className="truncate text-sm font-medium text-ink">
              {truncate(preview.social.title, 70)}
            </p>
            <p className="line-clamp-2 text-xs text-ink-muted">
              {truncate(preview.social.description, 120)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Social title" htmlFor="ogTitle" error={errors['ogTitle']}>
            <Input
              id="ogTitle"
              value={form.ogTitle}
              onChange={(event) => update('ogTitle', event.target.value)}
              maxLength={180}
              placeholder={truncate(preview.title, 40)}
              invalid={Boolean(errors['ogTitle'])}
            />
          </Field>

          <Field label="Social image URL" htmlFor="ogImageUrl" error={errors['ogImageUrl']}>
            <Input
              id="ogImageUrl"
              value={form.ogImageUrl}
              onChange={(event) => update('ogImageUrl', event.target.value)}
              maxLength={500}
              placeholder="Lead product image"
              invalid={Boolean(errors['ogImageUrl'])}
            />
          </Field>
        </div>

        <Field label="Social description" htmlFor="ogDescription" error={errors['ogDescription']}>
          <Textarea
            id="ogDescription"
            rows={2}
            value={form.ogDescription}
            onChange={(event) => update('ogDescription', event.target.value)}
            maxLength={400}
            placeholder={truncate(preview.description, 60)}
            invalid={Boolean(errors['ogDescription'])}
          />
        </Field>
      </div>

      <div className="space-y-2 pt-1">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Indexing</h4>
        <Checkbox
          label="No index — keep out of search results and the sitemap"
          checked={form.noIndex}
          onChange={(event) => update('noIndex', event.target.checked)}
        />
        <Checkbox
          label="No follow — do not pass ranking credit to links on this page"
          checked={form.noFollow}
          onChange={(event) => update('noFollow', event.target.checked)}
        />
      </div>
    </section>
  );
}

/**
 * The quantity-break ladder.
 *
 * A blank upper bound means "and above" — the same convention the API and the
 * CSV format use, so a merchandiser learns it once.
 */
function TierEditor({
  tiers,
  onChange,
  errors,
}: {
  tiers: PriceTier[];
  onChange: (tiers: PriceTier[]) => void;
  errors: Record<string, string>;
}) {
  const updateTier = (index: number, patch: Partial<PriceTier>): void =>
    onChange(tiers.map((tier, position) => (position === index ? { ...tier, ...patch } : tier)));

  const ladderError = errors['priceTiers'];

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Price tiers
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() =>
            onChange([
              ...tiers,
              {
                // Start the next tier just past the last one, which is what a
                // merchandiser almost always wants.
                minQuantity: (tiers.at(-1)?.maxQuantity ?? tiers.at(-1)?.minQuantity ?? 0) + 1,
                maxQuantity: null,
                pricePerUnit: 0,
              },
            ])
          }
        >
          <Plus className="size-3.5" aria-hidden="true" />
          Add tier
        </Button>
      </div>

      {ladderError && <p className="text-xs text-danger-600">{ladderError}</p>}

      {tiers.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-xs text-ink-subtle">
          No tiers. Buyers pay the wholesale price at every quantity.
        </p>
      ) : (
        <div className="space-y-2">
          {tiers.map((tier, index) => (
            <div key={index} className="flex items-end gap-2">
              <Field
                label="From"
                className="flex-1"
                error={errors[`priceTiers[${index}].minQuantity`]}
              >
                <Input
                  type="number"
                  min={1}
                  value={tier.minQuantity}
                  onChange={(event) =>
                    updateTier(index, { minQuantity: Number(event.target.value) })
                  }
                />
              </Field>

              <Field
                label="To"
                className="flex-1"
                error={errors[`priceTiers[${index}].maxQuantity`]}
              >
                <Input
                  type="number"
                  min={1}
                  placeholder="∞"
                  value={tier.maxQuantity ?? ''}
                  onChange={(event) =>
                    updateTier(index, {
                      maxQuantity: event.target.value === '' ? null : Number(event.target.value),
                    })
                  }
                />
              </Field>

              <Field
                label="₹ / unit"
                className="flex-1"
                error={errors[`priceTiers[${index}].pricePerUnit`]}
              >
                <Input
                  type="number"
                  min={0.01}
                  step={0.01}
                  value={tier.pricePerUnit}
                  onChange={(event) =>
                    updateTier(index, { pricePerUnit: Number(event.target.value) })
                  }
                />
              </Field>

              <button
                type="button"
                onClick={() => onChange(tiers.filter((_, position) => position !== index))}
                aria-label={`Remove tier starting at ${tier.minQuantity}`}
                className="mb-1 grid size-9 shrink-0 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SpecificationEditor({
  specs,
  onChange,
}: {
  specs: Array<{ name: string; value: string }>;
  onChange: (specs: Array<{ name: string; value: string }>) => void;
}) {
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">
          Specifications
        </h3>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => onChange([...specs, { name: '', value: '' }])}
        >
          <Plus className="size-3.5" aria-hidden="true" />
          Add row
        </Button>
      </div>

      {specs.map((spec, index) => (
        <div key={index} className="flex items-center gap-2">
          <Input
            aria-label={`Specification ${index + 1} name`}
            placeholder="Compartments"
            value={spec.name}
            onChange={(event) =>
              onChange(
                specs.map((entry, position) =>
                  position === index ? { ...entry, name: event.target.value } : entry,
                ),
              )
            }
          />
          <Input
            aria-label={`Specification ${index + 1} value`}
            placeholder="3 main, 2 zip"
            value={spec.value}
            onChange={(event) =>
              onChange(
                specs.map((entry, position) =>
                  position === index ? { ...entry, value: event.target.value } : entry,
                ),
              )
            }
          />
          <button
            type="button"
            onClick={() => onChange(specs.filter((_, position) => position !== index))}
            aria-label={`Remove specification ${index + 1}`}
            className="grid size-9 shrink-0 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
          >
            <Trash2 className="size-4" aria-hidden="true" />
          </button>
        </div>
      ))}
    </section>
  );
}

/**
 * Product images.
 *
 * Only available while editing: an image has to be attached to a product that
 * already exists, and the upload is a separate multipart request from the JSON
 * save, so there is nothing to attach it to until the product is created.
 */
function ImageManager({ product }: { product: AdminProductDetail }) {
  const upload = useUploadProductImage(product.id);
  const remove = useDeleteProductImage(product.id);
  const setSlot = useUpdateProductImage(product.id);
  const reorder = useReorderProductImages(product.id);
  const [type, setType] = useState<'MAIN' | 'GALLERY' | 'HOVER'>('GALLERY');
  const [dragging, setDragging] = useState<string | null>(null);

  const images = product.images;

  /**
   * Move an image to another position and send the whole resulting order.
   *
   * Positions are renumbered from zero rather than swapping two values, so a
   * gallery that has drifted (gaps, duplicates from concurrent edits) is
   * normalised by the act of reordering it.
   */
  const moveTo = (sourceId: string, targetId: string): void => {
    if (sourceId === targetId) return;

    const from = images.findIndex((image) => image.id === sourceId);
    const to = images.findIndex((image) => image.id === targetId);
    if (from === -1 || to === -1) return;

    const next = [...images];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved!);

    reorder.mutate(next.map((image, index) => ({ id: image.id, displayOrder: index })));
  };

  /** Keyboard equivalent of a drag, so reordering is not mouse-only. */
  const nudge = (imageId: string, direction: -1 | 1): void => {
    const index = images.findIndex((image) => image.id === imageId);
    const target = images[index + direction];

    if (target) moveTo(imageId, target.id);
  };

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Images</h3>
        {images.length > 1 && (
          <span className="text-[11px] text-ink-subtle">
            {reorder.isPending ? 'Saving order…' : 'Drag to reorder'}
          </span>
        )}
      </div>

      {upload.isError && <ErrorAlert>{errorMessage(upload.error)}</ErrorAlert>}
      {remove.isError && <ErrorAlert>{errorMessage(remove.error)}</ErrorAlert>}
      {reorder.isError && <ErrorAlert>{errorMessage(reorder.error)}</ErrorAlert>}
      {setSlot.isError && <ErrorAlert>{errorMessage(setSlot.error)}</ErrorAlert>}

      <div className="flex items-end gap-2">
        <Field label="Slot" className="w-32">
          <Select value={type} onChange={(event) => setType(event.target.value as typeof type)}>
            <option value="MAIN">Main</option>
            <option value="GALLERY">Gallery</option>
            <option value="HOVER">Hover</option>
          </Select>
        </Field>

        <label className="mb-1 inline-flex h-9 cursor-pointer items-center gap-2 rounded-lg border border-line px-4 text-sm font-medium text-ink-muted hover:border-brand-500 hover:text-brand-600">
          <ImagePlus className="size-4" aria-hidden="true" />
          {upload.isPending ? 'Uploading…' : 'Upload image'}
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="sr-only"
            disabled={upload.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload.mutate({ file, type, altText: product.name });
              // Clear the input so re-picking the same file still fires change.
              event.target.value = '';
            }}
          />
        </label>
      </div>

      {images.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-xs text-ink-subtle">
          No images yet.
        </p>
      ) : (
        <ul className="grid grid-cols-3 gap-2">
          {images.map((image, index) => (
            <li
              key={image.id}
              draggable
              onDragStart={() => setDragging(image.id)}
              onDragEnd={() => setDragging(null)}
              // Without preventDefault the browser refuses the drop outright.
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                if (dragging) moveTo(dragging, image.id);
                setDragging(null);
              }}
              className={cn(
                'group relative overflow-hidden rounded-lg border border-line',
                dragging === image.id && 'opacity-40',
                reorder.isPending && 'pointer-events-none',
              )}
            >
              <img
                src={`${apiBaseOrigin}${image.url}`}
                alt={image.altText ?? ''}
                className="aspect-square w-full cursor-grab bg-canvas object-cover active:cursor-grabbing"
              />

              <span className="absolute left-1 top-1">
                <Badge tone={image.type === 'MAIN' ? 'brand' : 'neutral'}>{image.type}</Badge>
              </span>

              <button
                type="button"
                onClick={() => remove.mutate(image.id)}
                aria-label={`Delete image ${index + 1}`}
                className="absolute right-1 top-1 grid size-7 place-items-center rounded-lg bg-surface/90 text-ink-subtle hover:text-danger-600"
              >
                <Trash2 className="size-3.5" aria-hidden="true" />
              </button>

              <div className="flex items-center justify-between gap-1 border-t border-line px-1 py-1">
                {/* Keyboard path to the same reorder, since drag-and-drop alone
                    is unusable without a mouse. */}
                <span className="flex">
                  <button
                    type="button"
                    onClick={() => nudge(image.id, -1)}
                    disabled={index === 0 || reorder.isPending}
                    aria-label={`Move image ${index + 1} earlier`}
                    className="grid size-6 place-items-center rounded text-ink-subtle hover:text-brand-600 disabled:opacity-30"
                  >
                    <ChevronLeft className="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={() => nudge(image.id, 1)}
                    disabled={index === images.length - 1 || reorder.isPending}
                    aria-label={`Move image ${index + 1} later`}
                    className="grid size-6 place-items-center rounded text-ink-subtle hover:text-brand-600 disabled:opacity-30"
                  >
                    <ChevronRight className="size-3.5" aria-hidden="true" />
                  </button>
                </span>

                {image.type !== 'MAIN' && (
                  <button
                    type="button"
                    onClick={() => setSlot.mutate({ imageId: image.id, type: 'MAIN' })}
                    disabled={setSlot.isPending}
                    // Promoting demotes the incumbent server-side, so this is
                    // always safe to offer.
                    className="rounded px-1 text-[10px] font-medium text-ink-subtle hover:text-brand-600"
                  >
                    Make main
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * Colour/size/material variants (spec §15).
 *
 * Each variant carries its own SKU and stock, so this is a list of rows rather
 * than a nested form: a variant is created against a product that already
 * exists, and is deleted independently of the parent's save.
 */
function VariantManager({ product }: { product: AdminProductDetail }) {
  const save = useSaveVariant(product.id);
  const remove = useDeleteVariant(product.id);
  const [editing, setEditing] = useState<string | null | undefined>(undefined);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-subtle">Variants</h3>
        <Button type="button" variant="ghost" size="sm" onClick={() => setEditing(null)}>
          <Plus className="size-3.5" aria-hidden="true" />
          Add variant
        </Button>
      </div>

      {remove.isError && <ErrorAlert>{errorMessage(remove.error)}</ErrorAlert>}

      {product.variants.length === 0 ? (
        <p className="rounded-lg border border-dashed border-line px-3 py-4 text-center text-xs text-ink-subtle">
          No variants. Buyers order the base product.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {product.variants.map((variant) => (
            <li
              key={variant.id}
              className="flex items-center gap-2 rounded-lg border border-line px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-ink">
                  {variant.name}
                  {!variant.isActive && (
                    <Badge tone="neutral" className="ml-1.5">
                      Inactive
                    </Badge>
                  )}
                </p>
                <p className="text-xs text-ink-subtle">
                  {variant.sku}
                  {variant.priceAdjustment !== 0 &&
                    ` · ${variant.priceAdjustment > 0 ? '+' : ''}${formatCurrency(variant.priceAdjustment)}`}
                  {variant.moq !== null && ` · MOQ ${formatNumber(variant.moq)}`}
                  {` · ${formatNumber(variant.availableQuantity)} available`}
                </p>
              </div>

              <button
                type="button"
                onClick={() => setEditing(variant.id)}
                aria-label={`Edit variant ${variant.name}`}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-subtle hover:bg-canvas hover:text-brand-600"
              >
                <Pencil className="size-4" aria-hidden="true" />
              </button>

              <button
                type="button"
                onClick={() => {
                  if (window.confirm(`Delete variant "${variant.name}"?`)) {
                    remove.mutate(variant.id);
                  }
                }}
                aria-label={`Delete variant ${variant.name}`}
                className="grid size-8 shrink-0 place-items-center rounded-lg text-ink-subtle hover:bg-danger-50 hover:text-danger-600"
              >
                <Trash2 className="size-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}

      {editing !== undefined && (
        <VariantEditor
          key={editing ?? 'new'}
          variant={product.variants.find((entry) => entry.id === editing) ?? null}
          save={save}
          onClose={() => setEditing(undefined)}
        />
      )}
    </section>
  );
}

/**
 * Inline variant editor.
 *
 * Rendered inside the product form, so it cannot be a nested `<form>` — HTML
 * forbids that, and the submit would bubble to the product save. The fields
 * commit through a button instead.
 */
function VariantEditor({
  variant,
  save,
  onClose,
}: {
  variant: ProductVariant | null;
  save: ReturnType<typeof useSaveVariant>;
  onClose: () => void;
}) {
  const [draft, setDraft] = useState(() => ({
    sku: variant?.sku ?? '',
    name: variant?.name ?? '',
    color: variant?.color ?? '',
    size: variant?.size ?? '',
    material: variant?.material ?? '',
    priceAdjustment: String(variant?.priceAdjustment ?? 0),
    moq: variant?.moq === null || variant?.moq === undefined ? '' : String(variant.moq),
    isActive: variant?.isActive ?? true,
  }));

  const errors = fieldErrors(save.error);
  const set = (key: keyof typeof draft, value: string | boolean): void =>
    setDraft((previous) => ({ ...previous, [key]: value }));

  const commit = (): void => {
    save.mutate(
      {
        variantId: variant?.id ?? null,
        body: {
          sku: draft.sku.trim(),
          name: draft.name.trim(),
          color: draft.color.trim() || null,
          size: draft.size.trim() || null,
          material: draft.material.trim() || null,
          priceAdjustment: Number(draft.priceAdjustment || 0),
          moq: draft.moq.trim() === '' ? null : Number(draft.moq),
          isActive: draft.isActive,
        },
      },
      { onSuccess: onClose },
    );
  };

  return (
    <div className="space-y-3 rounded-lg border border-brand-500/40 bg-canvas p-3">
      {save.isError && <ErrorAlert>{errorMessage(save.error)}</ErrorAlert>}

      <div className="grid grid-cols-2 gap-2">
        <Field label="SKU" required error={errors['sku']}>
          <Input
            value={draft.sku}
            onChange={(event) => set('sku', event.target.value)}
            invalid={Boolean(errors['sku'])}
          />
        </Field>
        <Field label="Name" required error={errors['name']}>
          <Input
            value={draft.name}
            placeholder="Black / Large"
            onChange={(event) => set('name', event.target.value)}
            invalid={Boolean(errors['name'])}
          />
        </Field>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Field label="Colour">
          <Input value={draft.color} onChange={(event) => set('color', event.target.value)} />
        </Field>
        <Field label="Size">
          <Input value={draft.size} onChange={(event) => set('size', event.target.value)} />
        </Field>
        <Field label="Material">
          <Input value={draft.material} onChange={(event) => set('material', event.target.value)} />
        </Field>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <Field
          label="Price adjustment"
          hint="Added to the tier price. May be negative."
          error={errors['priceAdjustment']}
        >
          <Input
            type="number"
            step={0.01}
            value={draft.priceAdjustment}
            onChange={(event) => set('priceAdjustment', event.target.value)}
          />
        </Field>
        <Field label="MOQ override" hint="Blank inherits the product's." error={errors['moq']}>
          <Input
            type="number"
            min={1}
            value={draft.moq}
            onChange={(event) => set('moq', event.target.value)}
          />
        </Field>
      </div>

      <Checkbox
        label="Active"
        checked={draft.isActive}
        onChange={(event) => set('isActive', event.target.checked)}
      />

      <div className="flex justify-end gap-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          disabled={save.isPending || !draft.sku.trim() || !draft.name.trim()}
          onClick={commit}
        >
          {save.isPending ? 'Saving…' : variant ? 'Save variant' : 'Add variant'}
        </Button>
      </div>
    </div>
  );
}
